import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { downloadPdf, downloadExcel } from "@/lib/files";
import { fmtDA } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileDown, FileSpreadsheet, Users, Loader2 } from "lucide-react";

export const Etat104Card = ({ societeId, exercice }) => {
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("all");
  const [du, setDu] = useState("");
  const [au, setAu] = useState("");
  const [annee, setAnnee] = useState(null);
  const year = annee || exercice;
  const SEM = [["S1", "01-01", "06-30"], ["S2", "07-01", "12-31"], ["AN", "01-01", "12-31"]];
  const pickSem = (y, d, f) => { setAnnee(null); setDu(`${y}-${d}`); setAu(`${y}-${f}`); };
  const pickYear = (y) => { setDu(""); setAu(""); setAnnee(annee === y ? null : y); };
  const { societes } = useApp();
  const slug = (n) => (n || "").replace(/\s+/g, "-");
  const pdfYear = (y, s) => downloadPdf(`/pdf/etat-clients?annee=${y}&societe_id=${s.id}`, `etat-104-${slug(s.name)}-${y}`);
  const xlsSociete = (s) => downloadExcel(`/pdf/etat-clients/excel?${new URLSearchParams({ ...params(), societe_id: s.societe_id }).toString()}`, `etat-104-${slug(s.societe)}`);
  const pdfSociete = (s) => downloadPdf(`/pdf/etat-clients?${new URLSearchParams({ ...params(), societe_id: s.societe_id }).toString()}`, `etat-104-${slug(s.societe)}`);

  const params = () => {
    const p = {};
    if (societeId !== "all") p.societe_id = societeId;
    if (du || au) {
      if (du) p.date_debut = du;
      if (au) p.date_fin = au;
    } else if (year !== "all") p.annee = year;
    if (client !== "all") p.client_id = client;
    return p;
  };

  useEffect(() => {
    setClient("all");
    api.get("/clients", { params: societeId !== "all" ? { societe_id: societeId } : {} })
      .then((r) => setClients(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => setClients([]));
  }, [societeId]);

  useEffect(() => {
    setData(null);
    let alive = true;
    api.get("/pdf/etat-clients/data", { params: params() }).then((r) => alive && setData(r.data)).catch(() => alive && setData([]));
    return () => { alive = false; };
  }, [societeId, exercice, client, du, au, annee]); // eslint-disable-line

  return (
    <div className="rounded-xl border bg-card p-5 mt-6" data-testid="dashboard-etat-104">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-primary" />
          <h3 className="font-heading font-bold">État 104 <span className="text-sm font-normal text-muted-foreground">({du || au ? `${du ? `du ${du.split("-").reverse().join("/")}` : ""} ${au ? `au ${au.split("-").reverse().join("/")}` : ""}`.trim() : year === "all" ? "toutes années" : year})</span></h3>
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full">
          {[2025, 2026].map((y) => (
            <div key={y} className="flex items-center gap-1">
              <Button size="sm" variant={String(year) === String(y) && !du && !au ? "default" : "outline"} onClick={() => pickYear(y)} data-testid={`etat104-year-${y}`}>{y}</Button>
              {societes.filter((s) => (data || []).some((d) => d.societe_id === s.id)).map((s) => (
                <Button key={s.id} size="sm" variant="ghost" className="gap-1" onClick={() => pdfYear(y, s)} data-testid={`etat104-pdf-${y}-${s.id}`}>
                  <FileDown className="h-4 w-4" /> {s.name} {y}
                </Button>
              ))}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2 w-full">
          <span className="text-xs text-muted-foreground">Période :</span>
          {[2025, 2026].flatMap((y) => SEM.map(([s, d, f]) => (
            <Button key={`${s}-${y}`} size="sm" variant={du === `${y}-${d}` && au === `${y}-${f}` ? "default" : "outline"}
              onClick={() => pickSem(y, d, f)} data-testid={`etat104-sem-${s.toLowerCase()}-${y}`}>
              {s === "AN" ? `Toute l'année ${y}` : `${s === "S1" ? "1er" : "2e"} semestre ${y}`}
            </Button>
          )))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-1 text-xs text-muted-foreground">Du
            <Input type="date" value={du} onChange={(ev) => setDu(ev.target.value)} className="h-9 w-[150px]" data-testid="etat104-date-debut" />
          </label>
          <label className="flex items-center gap-1 text-xs text-muted-foreground">Au
            <Input type="date" value={au} onChange={(ev) => setAu(ev.target.value)} className="h-9 w-[150px]" data-testid="etat104-date-fin" />
          </label>
          {(du || au) && <Button variant="ghost" size="sm" onClick={() => { setDu(""); setAu(""); }} data-testid="etat104-date-reset">Effacer dates</Button>}
          <div className="w-full sm:w-64">
            <Select value={client} onValueChange={setClient}>
              <SelectTrigger data-testid="etat104-client-filter"><SelectValue placeholder="Client" /></SelectTrigger>
              <SelectContent className="bg-popover max-h-64">
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.raison_sociale}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="gap-2" data-testid="etat104-pdf-btn"
            onClick={() => downloadPdf(`/pdf/etat-clients?${new URLSearchParams(params()).toString()}`, "etat-104")}>
            <FileDown className="h-4 w-4" /> PDF
          </Button>
          <Button variant="outline" size="sm" className="gap-2" data-testid="etat104-excel-btn"
            onClick={() => downloadExcel(`/pdf/etat-clients/excel?${new URLSearchParams(params()).toString()}`, "etat-104")}>
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </Button>
        </div>
      </div>
      {data === null ? <div className="flex justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> :
        data.map((s) => (
          <div key={s.societe_id} className="mb-5 last:mb-0" data-testid={`etat104-societe-${s.societe_id}`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-semibold mr-auto">{s.societe}</p>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => pdfSociete(s)} data-testid={`etat104-pdf-societe-${s.societe_id}`}>
                <FileDown className="h-4 w-4" /> PDF {s.societe}
              </Button>
              <Button variant="outline" size="sm" className="gap-2 ml-2" onClick={() => xlsSociete(s)} data-testid={`etat104-excel-societe-${s.societe_id}`}>
                <FileSpreadsheet className="h-4 w-4" /> Excel
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[800px]">
                <thead><tr className="text-left text-muted-foreground border-b">
                  {["N°", "Nom client", "Adresse", "RC", "NIF", "N° article"].map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}
                  <th className="py-2 font-medium text-right">Montant HT</th>
                  <th className="py-2 font-medium text-right">TVA</th>
                </tr></thead>
                <tbody className="divide-y">
                  {s.rows.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">Aucun client</td></tr>}
                  {s.rows.map((r, i) => (
                    <tr key={r.client_id} data-testid={`etat104-row-${s.societe_id}-${i + 1}`}>
                      <td className="py-2 pr-2 tabular text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-2 font-medium">{r.nom}</td>
                      <td className="py-2 pr-2">{r.adresse}</td>
                      <td className="py-2 pr-2">{r.rc}</td>
                      <td className="py-2 pr-2">{r.nif}</td>
                      <td className="py-2 pr-2">{r.article}</td>
                      <td className="py-2 tabular text-right">{fmtDA(r.ht)}</td>
                      <td className="py-2 tabular text-right">{fmtDA(r.tva)}</td>
                    </tr>
                  ))}
                  <tr className="font-bold border-t-2 border-primary/30" data-testid={`etat104-total-${s.societe_id}`}>
                    <td className="py-2" colSpan={6}>TOTAL</td>
                    <td className="py-2 tabular text-right">{fmtDA(s.rows.reduce((a, r) => a + r.ht, 0))}</td>
                    <td className="py-2 tabular text-right">{fmtDA(s.rows.reduce((a, r) => a + r.tva, 0))}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        ))}
    </div>
  );
};
